import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { SchoolOutlined, FlagRounded } from '@mui/icons-material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import {
  programEnrollmentApi,
  type ProgramEnrollmentStatus,
} from '../../../lib/api/program-enrollment.api';

const STATUS_COLOR: Record<ProgramEnrollmentStatus, 'success' | 'warning' | 'info' | 'default'> = {
  active: 'success',
  on_leave: 'warning',
  graduated: 'info',
  withdrawn: 'default',
};

interface Props {
  personId: string;
}

export function ProgressByProgramTab({ personId }: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();

  const [programNames, setProgramNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!currentTenantId) return;
    return onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
      setProgramNames(new Map(snap.docs.map((d) => [d.id, (d.data()['name'] as string) ?? d.id])));
    });
  }, [currentTenantId]);

  const enrollmentsQ = useQuery({
    queryKey: ['person-program-enrollments', currentTenantId, personId],
    enabled: !!currentTenantId && !!personId,
    queryFn: () =>
      programEnrollmentApi.listStudentProgramEnrollments({
        tenantId: currentTenantId!,
        studentId: personId,
      }),
    staleTime: 60_000,
  });

  if (enrollmentsQ.isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  const enrollments = enrollmentsQ.data?.items ?? [];

  if (enrollments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
        <SchoolOutlined sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
        <Typography variant="h6">{t('people.profile.progressByProgram.empty')}</Typography>
        <Typography variant="body2">{t('people.profile.progressByProgram.emptyDesc')}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {enrollments.map((e) => (
        <ProgramAuditCard
          key={e.programId}
          personId={personId}
          programId={e.programId}
          programName={programNames.get(e.programId) ?? e.programId}
        />
      ))}
    </Stack>
  );
}

function ProgramAuditCard({
  personId,
  programId,
  programName,
}: {
  personId: string;
  programId: string;
  programName: string;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const auditQ = useQuery({
    queryKey: ['person-degree-audit', currentTenantId, personId, programId],
    enabled: !!currentTenantId,
    queryFn: () =>
      programEnrollmentApi.getDegreeAudit({
        tenantId: currentTenantId!,
        studentId: personId,
        programId,
      }),
    staleTime: 60_000,
  });

  const audit = auditQ.data;
  const closed = audit?.status === 'graduated' || audit?.status === 'withdrawn';

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" fontWeight={700}>
            {programName}
          </Typography>
          {audit && (
            <Chip
              size="small"
              color={STATUS_COLOR[audit.status]}
              label={t(`career.status.${audit.status}`)}
            />
          )}
          {audit?.catalogYear && (
            <Chip
              size="small"
              variant="outlined"
              label={t('career.catalogYear', { year: audit.catalogYear })}
            />
          )}
        </Stack>
        {/* LOS CAMBIOS DE ESTADO NO SE HACEN ACÁ (H5): pausar, reactivar,
            egresar y retirar viven en la pestaña «Matrícula a programa», un
            solo lugar, con o sin el expediente de graduación. */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {t('programEnrollment.manageHint')}
        </Typography>

        {auditQ.isLoading && (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={24} />
          </Stack>
        )}

        {audit && (
          <Stack spacing={2.5} sx={{ mt: 2 }}>
            {/* Progress */}
            <Box>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography variant="h4" fontWeight={700}>
                  {audit.percentComplete}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('career.progress.credits', {
                    earned: audit.creditsEarned,
                    required: audit.creditsRequired,
                  })}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.max(0, audit.percentComplete))}
                sx={{ height: 8, borderRadius: 4, mt: 1 }}
              />
            </Box>

            {/* Recommended next term — NO PARA UNA INSCRIPCIÓN CERRADA. A quien
                ya se graduó o se retiró no se le recomiendan cursos para el
                próximo período: la tarjeta se contradecía sola. */}
            {!closed && audit.recommendedNextTerm.length > 0 && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {t('career.recommended.title')}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {audit.recommendedNextTerm.map((c) => (
                    <Chip key={c.courseId} icon={<FlagRounded />} label={c.courseName} />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Pending */}
            {audit.pending.length > 0 && (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {/* La lista SE QUEDA aunque esté cerrada —es justamente lo
                      que falta cuando alguien se graduó sin completar— pero no
                      puede seguir llamándose «pendientes». */}
                  {closed ? t('career.pending.titleClosed') : t('career.pending.title')}
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {audit.pending.map((c) => (
                    <Stack
                      key={c.courseId}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="body2">{c.courseName}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {!c.prerequisitesMet && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={t('career.pending.prereqMissing')}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {t('career.credits', { credits: c.credits })}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />
            <Typography variant="body2" color="text.secondary">
              {t('career.completed.title')}: {audit.completed.length}
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
